import { BillingPlanId } from '@/modules/billing/constants/BillingPlanId';
import { BillingPlanMapping } from '@/modules/billing/models/BillingPlanMapping';
import { BillingSubscriptionHistory } from '@/modules/billing/models/BillingSubscriptionHistory';
import { ModelType } from '/@/modules/core/app/constants/ModelType';
import { BaseEntityModel } from '/@/modules/core/app/models/abstracts/BaseEntityModel';

export class Billing extends BaseEntityModel {
  id: number | undefined;
  billingPlanMapping: Relationship<BillingPlanMapping> | null;
  estateId?: number;
  modelId?: number;
  modelType?: ModelType;
  userId?: number;
  nextBillingAt?: string;
  /** @deprecated DEV-21526 no longer written by the API; kept for backward compatibility only. */
  nextBillingAmount?: number;
  subscriptionPaidAt?: string;
  latestBillingSubscriptionHistory?: Relationship<BillingSubscriptionHistory> | null;
  billingSubscriptionHistories: BillingSubscriptionHistory[];

  constructor(model: Partial<Billing> = {}) {
    super(model);

    this.id = model.id;

    this.billingPlanMapping = BillingPlanMapping.mapModelOrNull(model.billingPlanMapping);
    this.nextBillingAt = model.nextBillingAt;
    this.nextBillingAmount = model.nextBillingAmount;
    this.subscriptionPaidAt = model.subscriptionPaidAt;
    this.latestBillingSubscriptionHistory = BillingSubscriptionHistory.mapModelOrNull(
      model.latestBillingSubscriptionHistory
    );
    this.billingSubscriptionHistories = BillingSubscriptionHistory.mapModels(
      model.billingSubscriptionHistories
    );
  }

  get sortedBillingSubscriptionHistories(): BillingSubscriptionHistory[] {
    return [...this.billingSubscriptionHistories].sort(
      (a: BillingSubscriptionHistory, b: BillingSubscriptionHistory) =>
        new Date(b.subscriptionStartsAt ?? 0).getTime() -
        new Date(a.subscriptionStartsAt ?? 0).getTime()
    );
  }

  get previousBillingSubscriptionHistory(): BillingSubscriptionHistory | null {
    return this.sortedBillingSubscriptionHistories[1] ?? null;
  }

  getModelName(): ModelType {
    return ModelType.BILLING;
  }

  get planName(): string | null {
    return this.billingPlanMapping?.planName ?? null;
  }

  get planId(): string {
    return this.billingPlanMapping?.planId ?? '';
  }

  get isEssentialPlan(): boolean {
    return this.billingPlanMapping?.isEssentialPlan ?? false;
  }

  get isStandardPlan(): boolean {
    return this.billingPlanMapping?.isStandardPlan ?? false;
  }

  get isBasicPlan(): boolean {
    return this.billingPlanMapping?.isBasicPlan ?? false;
  }

  get isCouplesBasicPlan(): boolean {
    return this.billingPlanMapping?.isCouplesBasicPlan ?? false;
  }

  get isIndividualBasicPlan(): boolean {
    return this.billingPlanMapping?.isIndividualStandardPlan ?? false;
  }

  get isIndividualStandardPlan(): boolean {
    return this.billingPlanMapping?.isIndividualStandardPlan ?? false;
  }

  get isSelfServePlan(): boolean {
    return !!this.billingPlanMapping?.isSelfServePlan;
  }

  get isCouplesSelfServePlan(): boolean {
    return !!this.billingPlanMapping?.isCouplesSelfServePlan;
  }

  get isPlusPlan(): boolean {
    return this.billingPlanMapping?.isPlusPlan ?? false;
  }

  get isCompletePlan(): boolean {
    return this.billingPlanMapping?.isCompletePlan ?? false;
  }

  get isProfessionalExecutorPlan(): boolean {
    return this.billingPlanMapping?.isProfessionalExecutorPlan ?? false;
  }

  get hasProfessionalExecutor(): boolean {
    return this.billingPlanMapping?.hasProfessionalExecutor ?? false;
  }

  get isAftercarePlan(): boolean {
    return this.billingPlanMapping?.isAftercarePlan ?? false;
  }

  get isCouplesPlan(): boolean {
    return [
      BillingPlanId.COUPLES_ESSENTIAL_PLAN,
      BillingPlanId.COUPLES_STANDARD_PLAN,
      BillingPlanId.COUPLES_PLUS_PLAN,
      BillingPlanId.COUPLES_COMPLETE_PLAN,
      BillingPlanId.COUPLES_PROFESSIONAL_EXECUTOR,
      BillingPlanId.COUPLES_CUSTOM
    ].includes(this.billingPlanMapping?.planId);
  }

  get isEstateRepresentativePlan(): boolean {
    return this.billingPlanMapping?.isEstateRepresentativePlan ?? false;
  }

  get isCompleteEstatePlan(): boolean {
    return this.billingPlanMapping?.isCompleteEstatePlan ?? false;
  }

  get isProbatePlan(): boolean {
    return this.billingPlanMapping?.isProbatePlan ?? false;
  }

  get isProbateAndTaxesPlan(): boolean {
    return this.billingPlanMapping?.isProbateAndTaxesPlan ?? false;
  }

  get isDigitalVaultPlan(): boolean {
    return this.billingPlanMapping?.isDigitalVaultPlan ?? false;
  }

  get isSubscriptionPaid(): boolean {
    if (!this.nextBillingAt) {
      return false;
    }

    return new Date(this.nextBillingAt) > new Date();
  }

  get isEstateHealthCheckRenewalPaid(): boolean {
    if (!this.nextBillingAt) {
      return false;
    }

    const nextBilling: Date = new Date(this.nextBillingAt);
    const today: Date = new Date();
    const daysUntilBilling: number = Math.floor(
      (nextBilling.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)
    );

    return daysUntilBilling > 10 && this.isSubscriptionPaid;
  }

  get isGuidedPlan(): boolean {
    return !!this.billingPlanMapping?.isGuidedPlan;
  }
}
